import { deleteFetcher, getFetcher, postFetcher } from '@/api/fetchers';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import type { Service, Services } from '@/schemas/API/GET/service/response';
import { Paper, Stack, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { DropDownList } from '@/components/DropDownList/DropDownList';
import { useNavigate } from 'react-router-dom';
import { IconTrash } from '@tabler/icons-react';
import { FormModal } from '@/components/Modal/FormModal';
import { goodNotification } from '@/components/Notification/Notification';
import {
  displayError,
  displayErrorOnUseState,
} from '@/utils/error/errorHandler';

export const ServicePage = (): JSX.Element => {
  const navigate = useNavigate();

  const [nameError, setNameError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [priceError, setPriceError] = useState('');
  const [formData, setFormData] =
      useState({ code: '', price: '', desc: '', name: '' });

  const [isNewServiceModalOpen, setNewServiceModalOpen] = useState(false);
  const [isConfirmDeleteServiceModalOpen, setIsConfirmDeleteServiceModalOpen] =
    useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null
  );

  const { data: services } = useSWR<Services>('/services', getFetcher);
  const { data: selectedService } = useSWR<Service>(
    selectedServiceId ? `/services/${selectedServiceId}` : null,
    getFetcher
  );

  function closeModal() {
    setFormData({ code: '', price: '', desc: '', name: '' });
    mutate('/services');
    setNameError('');
    setCodeError('');
    setPriceError('');
  }

  const openNewServiceModal = () => {
    setNewServiceModalOpen(true);
  };

  const closeNewServiceModal = () => {
    setNewServiceModalOpen(false);
    closeModal();
  };

  const openConfirmDeleteServiceModal = (id: number) => {
    setSelectedServiceId(id);
    setIsConfirmDeleteServiceModalOpen(true);
  };

  const closeConfrimDeleteServiceModal = () => {
    setIsConfirmDeleteServiceModalOpen(false);
    closeModal();
  };

  function onServiceClick(id: number): void {
    navigate(`/services/${id}/details`);
  }

  const deleteService = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    try {
      await deleteFetcher(`/services/${selectedServiceId}`);
      goodNotification('Úspěch!', 'Služba je smazaná');
    } catch (err) {
      displayError(err);
    } finally {
      closeConfrimDeleteServiceModal();
    }
  };

  async function createNewService(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNameError('');
    setCodeError('');
    setPriceError('');
    try {
      const payload: Partial<Service> = {
        name: formData.name,
        code: formData.code,
      };

      if (formData.price) {
        payload.price = parseFloat(formData.price); // Convert string to number
      }
      if (formData.desc) {
        payload.desc = formData.desc;
      }

      await postFetcher('/services', payload);
      goodNotification('Úspěch!', 'Služba je vytvořená');
      closeNewServiceModal();
    } catch (err) {
      displayErrorOnUseState(err, [
        { field: 'name', setState: setNameError },
        { field: 'code', setState: setCodeError },
        { field: 'price', setState: setPriceError },
      ]);
    }
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  return (
      // todo tests
    <Stack className="h-full p-8 overflow-auto">
      <PageHeader name="Služby" />

      <Paper withBorder radius="md" className="p-4 flex-1 overflow-auto">
        <DropDownList
          title="Seznam služeb"
          list={services}
          createNewItem={() => {
            openNewServiceModal();
          }}
          createNewItemButtonText="Nová služba"
          onItemClick={onServiceClick}
          itemToolBar={[
            {
              index: 0,
              function: (item: Service) => {
                openConfirmDeleteServiceModal(item.id);
                setSelectedServiceId(item.id);
              },
              icon: <IconTrash />,
            },
          ]}
          loading={services === undefined}
        />
      </Paper>

      <FormModal
        opened={isConfirmDeleteServiceModalOpen}
        onClose={closeConfrimDeleteServiceModal}
        onConfirm={deleteService}
        confirmText="Smazat"
        confirmButtonColor="red"
      >
        <div className="text-lg text-center">
          Opravdu si přejete smazat službu{' '}
          <span className="font-bold italic">{selectedService?.name}</span> ?
        </div>
      </FormModal>
      <FormModal
        confirmButtonColor="green"
        title={'Nová služba'}
        opened={isNewServiceModalOpen}
        onClose={closeNewServiceModal}
        onConfirm={createNewService}
      >
        <TextInput
          name="name"
          variant="filled"
          label="Název služby"
          size="md"
          radius="md"
          value={formData.name}
          error={nameError}
          onChange={handleChange}
          withErrorStyles={false}
        />
        <TextInput
          variant="filled"
          label="Kód služby"
          name="code"
          size="md"
          radius="md"
          value={formData.code}
          error={codeError}
          onChange={handleChange}
          withErrorStyles={false}
        />
        <TextInput
            variant="filled"
            label="Cena služby"
            name="price"
            size="md"
            radius="md"
            value={formData.price}
            error={priceError}
            withErrorStyles={false}
        />
        <Textarea
          name="desc"
          variant="filled"
          label="Popis služby"
          autosize
          minRows={4}
          maxRows={8}
          size="md"
          radius="md"
          value={formData.desc}
          onChange={handleChange}
          withErrorStyles={false}
        />
      </FormModal>
    </Stack>
  );
};

export {};
